<!--
 * @Description: 访客计划 - 新增编辑表单
 * @Author: mdb
 * @Date: 2022-07-19 11:15:19
 * @LastEditTime: 2022-12-27 10:40:11
 * @LastEditors: mdb
-->
<template>
  <a-form scrollToFirstError>
    <dc-layout-card :title="props.title" :span="6">
      <dc-form-item label="客户名称" v-bind="validateInfos.visitRefId">
        <ClueAndCustomerSelect
          appId="cos_lbu"
          v-model:value="dataForm.visitRefId"
          v-model:label="dataForm.customerName"
          :disabled="!props.isEdit || !!props.saleChanceRefId"
          :view="props.type === 'view'"
          @change="handleCustomerNameChange"
        />
      </dc-form-item>
      <dc-form-item label="客户ID" v-if="props.type === 'view'">
        <CosInput v-model:value="dataForm.visitRefId" view />
      </dc-form-item>
      <dc-form-item label="计划标题" v-bind="validateInfos.title">
        <CosInput
          v-model:value.trim="dataForm.title"
          :maxlength="100"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
        />
      </dc-form-item>
      <dc-form-item label="拜访方式" v-bind="validateInfos.mode">
        <ParamCodesSelect
          v-model:value="dataForm.mode"
          :options="paramCodeData.lbu_follow_mode"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
          allowClear
        />
      </dc-form-item>
      <dc-form-item label="计划拜访时间" v-bind="validateInfos.startTime">
        <CosDateTimeRagePicker
          v-model:startTime="dataForm.startTime"
          v-model:endTime="dataForm.endTime"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
          :minuteStep="15"
        />
      </dc-form-item>
      <dc-form-item label="拜访目的" v-bind="validateInfos.purpose">
        <ParamCodesSelect
          v-model:value="dataForm.purpose"
          :options="paramCodeData.lbu_follow_purpose"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
          allowClear
        />
      </dc-form-item>
      <dc-form-item label="联系人" v-bind="validateInfos.contactId">
        <CosSelect
          v-model:value="dataForm.contactId"
          :fieldNames="{ label: 'name', value: 'id' }"
          :options="contactEnum"
          :disabled="!props.isEdit"
          allowClear
          :view="props.type === 'view'"
          @change="handleContactChange"
        />
      </dc-form-item>
      <dc-form-item label="关联商机">
        <CosSelect
          v-model:value="dataForm.chanceId"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
          allowClear
          @change="handleChanceChange"
        >
          <a-select-option v-for="item in chanceEnum" :key="item.chanceId" :value="item.chanceId">
            <BusinessName appId="cos_lbu" v-model:label="item.label" v-model:value="item.saleChanceName" />
          </a-select-option>
        </CosSelect>
      </dc-form-item>
      <dc-form-item label="访客提醒" v-bind="validateInfos.remindType">
        <CosSelect
          v-model:value="dataForm.remindType"
          :options="remindTypeEnum"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
          allowClear
        />
      </dc-form-item>
      <dc-form-item label="提醒时间" v-bind="validateInfos.remindTime" v-if="isOtherTime">
        <a-date-picker
          v-if="props.type !== 'view'"
          style="width: 100%"
          v-model:value="dataForm.remindTime"
          format="YYYY/MM/DD HH:mm:ss"
          value-format="YYYY/MM/DD HH:mm:ss"
          show-time
          :disabled="!props.isEdit"
          placeholder="请选择"
        />
        <CosInputView :value="dataForm.remindTime" v-else />
      </dc-form-item>
      <template v-if="props.type === 'view'">
        <dc-form-item label="创建人">
          <CosInput v-model:value="dataForm.createByName" view />
        </dc-form-item>
        <dc-form-item label="创建时间">
          <CosInput v-model:value="dataForm.createTime" view />
        </dc-form-item>
        <dc-form-item label="更新人">
          <CosInput v-model:value="dataForm.updateByName" view />
        </dc-form-item>
        <dc-form-item label="更新时间">
          <CosInput v-model:value="dataForm.updateTime" view />
        </dc-form-item>
      </template>
      <a-col :span="24" />
      <dc-form-item label="备注" :span="12">
        <CosTextarea
          v-model:value="dataForm.remark"
          :maxlength="2000"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
        />
      </dc-form-item>
    </dc-layout-card>
  </a-form>
</template>

<script setup lang="ts">
import { reactive, watchEffect, watch, toRaw, ref, onMounted } from 'vue'
import { cloneDeep, isEqual } from 'lodash-es'
import { Form } from 'ant-design-vue'
import CosDateTimeRagePicker from '@/components/CosDateTimeRagePicker/index.vue'
import ClueAndCustomerSelect from '@/components/ClueAndCustomerSelect/index.vue'
import BusinessName from '@/components/BusinessName/index.vue'
import { remindTypeEnum } from '../dataConfig'
import { listByParamCodes } from '@/api/common'
import { businessSelectListByRefId } from '@/api/yuntuSales/chanceManage'
import { customerContactList } from '@/api/yuntuSales/customerCenter'
import CosInputView from '@/components/Antd/CosView/input.vue'
import { selectForNumberValidate } from '@/utils/validate'
import { paramCodesDataFilter } from '@/utils/index'
import type { ParamCodeItem } from '@/typings/common'
import type { LbuVisitorProgramEditParams } from '@/typings/api/followUpManage'
import dayjs from 'dayjs'

const useForm = Form.useForm

const props = defineProps({
  value: Object,
  title: String,
  saleChanceRefId: [String, Number], // 客户id，当传入有值的时候需要设置客户名称不可选
  isEdit: {
    type: Boolean,
    default: true
  },
  type: {
    type: String,
    default: 'add'
  }
})

const paramCodeData: {
  [key: string]: ParamCodeItem[]
} = reactive({
  lbu_follow_purpose: [], // 拜访目的
  lbu_follow_mode: [] // 拜访方式（跟进方式）
})
// 获取业务参数
const getByParamCodes = () => {
  listByParamCodes({
    appId: 'cos_lbu',
    paramCodes: ['lbu_follow_purpose', 'lbu_follow_mode']
  }).then((res) => {
    if (props.type !== 'view') {
      // 递归过滤业务参数状态
      res.data = paramCodesDataFilter(res.data, 0)
    }
    Object.assign(paramCodeData, res.data)
  })
}
onMounted(() => {
  if (dataForm.startTime === undefined) {
    dataForm.startTime = dayjs().format('YYYY/MM/DD HH:mm:ss')
    dataForm.endTime = dataForm.startTime
  }
  getByParamCodes()
})

const dataForm = reactive<LbuVisitorProgramEditParams>({
  visitRefId: undefined,
  title: undefined,
  mode: undefined,
  startTime: undefined,
  purpose: undefined,
  contactId: undefined,
  remindType: 1,
  remindTime: undefined
})
const isOtherTime = ref(false) // 是否其它时间
const formRules = reactive({
  visitRefId: [{ required: true, message: '请选择客户名称' }],
  title: [{ required: true, message: '请输入计划标题' }],
  mode: [{ required: true, validator: selectForNumberValidate, message: '请选择拜访方式' }],
  startTime: [{ required: true, message: '请选择拜访时间' }],
  purpose: [{ required: true, validator: selectForNumberValidate, message: '请选择拜访目的' }],
  contactId: [{ required: true, validator: selectForNumberValidate, message: '请选择联系人' }],
  remindType: [{ required: true, validator: selectForNumberValidate, message: '请选择访客提醒' }],
  remindTime: [{ required: isOtherTime, message: '请选择提醒时间' }]
})

// 获取关联商机
const chanceEnum = ref<any[]>([])
const getChanceEnum = (id: string) => {
  businessSelectListByRefId(id).then((res) => {
    chanceEnum.value = res.data
  })
}
// 获取联系人
const contactEnum = ref<any[]>([])
const getContactEnum = (id: string) => {
  const params = { contactRefId: id, pageNumber: 1, pageSize: 200 }
  customerContactList(params).then((res) => {
    contactEnum.value = res.data.items
  })
}
const handleCustomerNameChange = (value?: any) => {
  dataForm.chanceName = undefined
  dataForm.chanceId = undefined
  dataForm.contactId = undefined
  setTimeout(() => clearValidate(['contactId']), 0)
  if (!value) {
    chanceEnum.value = []
    contactEnum.value = []
  }
}
const handleContactChange = (value?: any) => {
  dataForm.contactName = value ? contactEnum.value.find((e) => e.id === value)?.name : undefined
}
const handleChanceChange = (value?: any) => {
  dataForm.chanceName = value ? chanceEnum.value.find((e) => e.chanceId === value)?.label : undefined
}

const emit = defineEmits(['update:value'])
watchEffect(() => {
  if (!props.value) return
  // 如果值相同则不更新
  const isSame = isEqual(toRaw(dataForm), toRaw(props.value))
  if (isSame) return
  const clone = cloneDeep(props.value)
  Object.assign(dataForm, clone)
})
watch(
  dataForm,
  (val) => {
    emit('update:value', val)
  },
  { deep: true }
)
// 回显数据监听客户名称变化
watch(
  () => dataForm.visitRefId,
  (val) => {
    if (val) {
      getChanceEnum(val)
      getContactEnum(val)
    }
  },
  { immediate: true }
)
// 详情页面回显时，当联系人已经被删除时需要特殊处理
watch(
  () => dataForm.contactId,
  (val) => {
    if (props.type === 'view' && val) {
      const hasContact = contactEnum.value.some((e) => e.id === val)
      if (!hasContact) dataForm.contactId = dataForm.contactName
    }
  }
)

// 监听判断“提醒时间”是否必填
// 当访客提醒选择其他时间时，“提醒时间”字段必填
watch(
  () => dataForm.remindType,
  (val) => {
    setTimeout(() => clearValidate(['remindTime']), 0)
    isOtherTime.value = val === 4
    if (val && val !== 4) dataForm.remindTime = undefined
  },
  { immediate: true }
)

const { resetFields, validate, clearValidate, validateInfos } = useForm(dataForm, formRules)
defineExpose({ resetFields, validate })
</script>
<script lang="ts">
export default { name: 'VisitorProgramForm' }
</script>

<style lang="scss" scoped></style>
